import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getCart, addToCart as apiAddToCart, updateCartItem, removeFromCart as apiRemoveFromCart, clearCart as apiClearCart } from '../services/api';
import { useSettings } from '../context/SettingsContext';
import { useToast } from '../hooks/use-toast';
import { useRecaptcha } from '../hooks/useRecaptcha';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const { toast } = useToast();
  const { settings } = useSettings();
  const [cart, setCart] = useState({ items: [] });
  const [loading, setLoading] = useState(false);
  const { execute: executeRecaptcha, enabled: recaptchaEnabled } = useRecaptcha();

  const getRecaptchaToken = useCallback(async (action) => {
    const siteKey = settings.recaptcha_site_key;
    if (!siteKey || !window.grecaptcha) return '';
    try {
      return await window.grecaptcha.execute(siteKey, { action });
    } catch {
      return '';
    }
  }, [settings.recaptcha_site_key]);

  // Load cart on mount
  useEffect(() => {
    loadCart();
  }, []);

  const loadCart = async () => {
    try {
      const cartData = await getCart();
      setCart(cartData);
    } catch (error) {
      console.error('Error loading cart:', error);
    }
  };

  const addToCart = async (productId, quantity = 1) => {
    setLoading(true);
    try {
      const recaptchaToken = await getRecaptchaToken('add_to_cart');
      await apiAddToCart(productId, quantity, recaptchaToken);
      await loadCart();
      toast({
        title: "Added to cart",
        description: "Product has been added to your cart",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add product to cart",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (productId, quantity) => {
    setLoading(true);
    try {
      await updateCartItem(productId, quantity);
      await loadCart();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update cart",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (productId) => {
    setLoading(true);
    try {
      await apiRemoveFromCart(productId);
      await loadCart();
      toast({
        title: "Removed",
        description: "Product removed from cart",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to remove product",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const clearCart = async () => {
    setLoading(true);
    try {
      await apiClearCart();
      await loadCart();
      toast({
        title: "Cart cleared",
        description: "All items removed from cart",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to clear cart",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const getCartItemsCount = () => {
    return cart.items.reduce((total, item) => total + item.quantity, 0);
  };

  const value = {
    cart,
    loading,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    getCartItemsCount,
    refreshCart: loadCart
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};